import type { ReactNode } from 'react';
import { useEffect } from 'react';
import type { Takeover } from '../chat/AuthChatPanel';
import { AuthChatPanel } from '../chat/AuthChatPanel';
import { usePanelOverlay } from '../chat/use-panel-overlay';
import { PRIVACY_URL, TERMS_URL } from '../legal-links';
import markUrl from '@/public/logo/mark-color.svg';

type AuthLayoutApi = {
  /** Opens the full screen signup chat. */
  openSignup: () => void;
};

type AuthLayoutProps = {
  children: ReactNode;
  /** A node, or a function when the footer needs to open the signup chat. */
  footer?: ReactNode | ((api: AuthLayoutApi) => ReactNode);
  /** Mount with the signup chat already open (the /auth/signup route). */
  startInSignup?: boolean;
  /**
   * Full screen content in place of the signup chat, mounted open (the /auth/verify
   * route). Its `close` slides the panel away to reveal the form. Passed at mount it
   * starts open; passed later (an invitation accepted on this page) the panel expands
   * to cover the screen when it arrives.
   */
  takeover?: Takeover;
  /** Accessible name of the takeover dialog. */
  takeoverLabel?: string;
  /** Fires after the full screen panel has closed and is back in its slot. */
  onClosed?: () => void;
};

/**
 * The auth desk: the form on the left, the announcements chat on the right, on one
 * white page with a 10px gutter. Below about 900px the two wrap and the chat sits
 * under the form, which is where it belongs: signing in is why someone opened the
 * page, and product news should not push the password field down to get itself
 * read.
 *
 * The chat opens into the signup flow, which is why the layout owns the overlay
 * rather than the panel: the footer link that starts signup lives in the form
 * column, and has to reach it.
 *
 * GlobalAnnouncement is deliberately absent. The bar is still mounted in the
 * dashboard shell, but here the chat is the announcement surface, and the two
 * would otherwise say different things a hundred pixels apart.
 */
export const AuthLayout = ({
  children,
  footer,
  startInSignup = false,
  takeover,
  takeoverLabel,
  onClosed,
}: AuthLayoutProps) => {
  const overlay = usePanelOverlay({ startOpen: startInSignup || !!takeover, onClosed });

  const hasTakeover = !!takeover;
  useEffect(() => {
    // open() ignores a panel that is already open, so a takeover mounted open is untouched.
    if (hasTakeover) overlay.open();
  }, [hasTakeover]);

  const footerNode = typeof footer === 'function' ? footer({ openSignup: overlay.open }) : footer;

  return (
    <div className="bg-panel flex min-h-screen flex-wrap gap-2.5 p-2.5">
      {/* inert while the signup chat covers the screen. The form is still mounted
          underneath (closing is an animation back to a docked panel, not a
          remount), so without this Tab walks into fields nobody can see, and a
          screen reader reads two sets of email and password inputs. */}
      <main
        inert={overlay.phase !== 'docked'}
        className="flex min-w-0 flex-[1_1_420px] flex-col px-8 pt-[26px] pb-[18px]"
      >
        <div className="mx-auto flex w-full max-w-[360px] flex-1 flex-col justify-center gap-6 py-8 min-[910px]:py-14">
          {/* The bare mark, no wordmark and no container. */}
          <img className="mb-[18px] h-8 w-auto self-start" src={markUrl} alt="Pertento" />

          {children}

          {footerNode && <div className="text-dim text-meta leading-none">{footerNode}</div>}
        </div>

        {/* Two side by side: at the bottom of the form column. */}
        <LegalFooter className="hidden min-[910px]:flex" />
      </main>

      <AuthChatPanel overlay={overlay} takeover={takeover} takeoverLabel={takeoverLabel} />

      {/* Stacked: at the very bottom of the page, under the chat. Inside the form
          column it sat between the form and the chat, in the way of the one thing
          the chat is there for. The same links rendered twice, one hidden per layout. */}
      <LegalFooter inert={overlay.phase !== 'docked'} className="basis-full px-3 pt-2 pb-1 min-[910px]:hidden" />
    </div>
  );
};

type LegalFooterProps = { className: string; inert?: boolean };

// `inert` for the copy outside <main>, which would otherwise stay tabbable behind the
// full screen panel.
export const LegalFooter = ({ className, inert }: LegalFooterProps) => (
  <footer inert={inert} className={`log-line text-dim items-center justify-between ${className}`}>
    <span>© Pertento 2026</span>
    <span className="flex items-center gap-3">
      <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
        Privacy
      </a>
      {/* Decorative: the two are already separate links to a screen reader. */}
      <span aria-hidden="true">|</span>
      <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
        Terms
      </a>
    </span>
  </footer>
);
